import enums from "../enums";

const initialChannelMessages = {
	byId: {},
	ids: [],
	removedIds: [],
};

const upsertMessage = (channelState, message) => {
	if (!message?.messageId) {
		return channelState;
	}

	const exists = channelState.byId[message.messageId];
	const removedIds = (channelState.removedIds ?? []).filter((id) => id !== message.messageId);

	return {
		byId: {
			...channelState.byId,
			[message.messageId]: message,
		},
		ids: exists ? channelState.ids : [...channelState.ids, message.messageId],
		removedIds,
	};
};

const markMessageRemoved = (channelState, messageId) => {
	if (!messageId) {
		return channelState;
	}

	const { [messageId]: removed, ...byId } = channelState.byId;
	const removedIds = (channelState.removedIds ?? []).includes(messageId)
		? channelState.removedIds
		: [...(channelState.removedIds ?? []), messageId];

	return {
		byId,
		ids: channelState.ids.filter((id) => id !== messageId),
		removedIds,
	};
};

const getChatSocketPayload = (msg) => {
	if (!msg || typeof msg !== "object") {
		return null;
	}

	const channelId = msg.channelId ?? msg.data?.channelId;
	const event = msg.event ?? msg.data?.event;
	const message = msg.message ?? msg.data?.message;
	const messageId = msg.messageId ?? msg.data?.messageId;

	if (!channelId) {
		return null;
	}

	return { channelId, event, message, messageId };
};

const chat = (
	state = {
		isPanelOpen: false,
		activeChannelId: null,
		channelMessages: {},
		isChatEnabled: true,
		eligibilityLoaded: false,
	},
	action
) => {
	switch (action.type) {
		case enums.CHAT_SET_PANEL_OPEN:
			return {
				...state,
				isPanelOpen: Boolean(action.payload),
			};

		case enums.CHAT_SET_CHANNEL:
			return { ...state, activeChannelId: action.payload ?? null };

		case enums.CHAT_SET_ELIGIBILITY:
			return {
				...state,
				isChatEnabled: Boolean(action.payload?.chatEnabled),
				eligibilityLoaded: Boolean(action.payload?.loaded),
			};

		case enums.CHAT_CLEAR_CHANNEL_MESSAGES: {
			const channelId = action.payload;

			if (!channelId) {
				return state;
			}

			return {
				...state,
				channelMessages: {
					...state.channelMessages,
					[channelId]: { ...initialChannelMessages },
				},
			};
		}

		case enums.CHAT_UPSERT_MESSAGE: {
			const { channelId, message } = action.payload ?? {};

			if (!channelId || !message?.messageId) {
				return state;
			}

			const channelState = state.channelMessages[channelId] ?? initialChannelMessages;

			return {
				...state,
				channelMessages: {
					...state.channelMessages,
					[channelId]: upsertMessage(channelState, message),
				},
			};
		}

		case enums.CHAT_REMOVE_MESSAGE: {
			const { channelId, messageId } = action.payload ?? {};

			if (!channelId || !messageId) {
				return state;
			}

			const channelState = state.channelMessages[channelId] ?? initialChannelMessages;

			return {
				...state,
				channelMessages: {
					...state.channelMessages,
					[channelId]: markMessageRemoved(channelState, messageId),
				},
			};
		}

		case "chat": {
			const socketPayload = getChatSocketPayload(action.msg);

			if (!socketPayload) {
				return state;
			}

			const { channelId, event, message, messageId } = socketPayload;
			const channelState = state.channelMessages[channelId] ?? initialChannelMessages;
			const normalizedEvent = String(event ?? "").toLowerCase();

			if (
				(normalizedEvent === "messagecreated" || normalizedEvent === "message_created") &&
				message
			) {
				return {
					...state,
					channelMessages: {
						...state.channelMessages,
						[channelId]: upsertMessage(channelState, message),
					},
				};
			}

			if (
				(normalizedEvent === "messageremoved" || normalizedEvent === "message_removed") &&
				messageId
			) {
				return {
					...state,
					channelMessages: {
						...state.channelMessages,
						[channelId]: markMessageRemoved(channelState, messageId),
					},
				};
			}

			return state;
		}

		default:
			return state;
	}
};

export default chat;
